import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaCheck,
  FaEdit,
  FaFileAlt,
  FaGraduationCap,
  FaTimes,
  FaUniversity,
  FaUser,
} from "react-icons/fa";
import Swal from "sweetalert2";
import {
  updateApplicationStudentCourseDetails,
  upsertCommissionInquiryFees,
} from "../commissionsApi";
import { formatCurrency, getInitials } from "./commissionDetailConstants";
import { formatDisplayDate } from "../commissionsConstants";

/** Only these fields can be edited */
const EDITABLE_KEYS = new Set([
  "studentId",
  "studentBirthDate",
  "start",
  "end",
  "tuitionFee",
  "commissionable_tuition_fee",
  "applicationFee",
  "materialFee",
]);

const FEE_EDITABLE_KEYS = new Set(["applicationFee", "materialFee"]);

function toInputDate(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const slash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const [, day, month, year] = slash;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return "";
}

function displayDate(value) {
  if (!value) return "—";
  return formatDisplayDate(toInputDate(value) || value) || value;
}

function displayTuition(value, currency = "AUD") {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return formatCurrency(num, currency);
}

function resolveDurationLabel(start, end) {
  const startDate = toInputDate(start);
  const endDate = toInputDate(end);
  if (!startDate || !endDate) return null;
  const a = new Date(startDate);
  const b = new Date(endDate);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a)
    return null;
  const months =
    (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (months <= 0) return null;
  if (months === 1) return "1 month";
  if (months < 12) return `${months} months`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (rem === 0) return years === 1 ? "1 year" : `${years} years`;
  return `${years}y ${rem}m`;
}

function buildInitialValues(inquiry = {}) {
  return {
    providerName: inquiry.providerName || "",
    studentId: inquiry.studentId || "",
    clientName: inquiry.clientName || "",
    studentBirthDate: toInputDate(inquiry.studentBirthDate),
    course: inquiry.course || "",
    start: toInputDate(inquiry.start),
    end: toInputDate(inquiry.end),
    intake: inquiry.intake || "",
    tuitionFee: inquiry.tuitionFee || "",
    commissionable_tuition_fee: inquiry.commissionable_tuition_fee || "",
    applicationFee:
      inquiry.applicationFee != null && inquiry.applicationFee !== ""
        ? String(inquiry.applicationFee)
        : "",
    materialFee:
      inquiry.materialFee != null && inquiry.materialFee !== ""
        ? String(inquiry.materialFee)
        : "",
  };
}

function Chip({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-white text-slate-600",
    blue: "border-[#007BFF]/20 bg-[#007BFF]/8 text-[#007BFF]",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function FeeStatusBadge({ status = "none" }) {
  if (status === "none") return null;

  const isPaid = status === "paid";
  return (
    <span
      className={`absolute right-0 top-0 rounded px-1 py-px text-[7px] font-bold uppercase leading-none tracking-wide ${
        isPaid
          ? "bg-emerald-100 text-emerald-700"
          : "bg-rose-100 text-rose-700"
      }`}
    >
      {isPaid ? "Paid" : "Unpaid"}
    </span>
  );
}

function FieldInput({
  label,
  type,
  value,
  onChange,
  disabled,
  className = "",
  inputRef = null,
}) {
  return (
    <label className={`block min-w-0 ${className}`}>
      <span className="mb-0.5 block text-[9px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </span>
      <input
        ref={inputRef}
        type={type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px] font-medium text-slate-800 outline-none transition hover:border-slate-300 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-70"
      />
    </label>
  );
}

function SectionLabel({ icon: Icon, children, hint = null }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
        {Icon ? <Icon size={9} className="text-[#007BFF]" /> : null}
        {children}
      </div>
      {hint ? (
        <span className="text-[9px] font-medium text-slate-400">{hint}</span>
      ) : null}
    </div>
  );
}

function CommissionDetailProfileSidebar({
  inquiry = {},
  student,
  provider,
  course,
  application,
  params = {},
  currency = "AUD",
  onSaved,
  onOpenDocuments,
  documentCount = 0,
}) {
  const initialValues = useMemo(() => buildInitialValues(inquiry), [inquiry]);
  const [values, setValues] = useState(initialValues);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const firstInputRef = useRef(null);

  useEffect(() => {
    setValues(initialValues);
    setIsEditing(false);
  }, [initialValues]);

  useEffect(() => {
    if (!isEditing) return undefined;
    const timer = window.setTimeout(() => firstInputRef.current?.focus(), 40);
    return () => window.clearTimeout(timer);
  }, [isEditing]);

  const isDirty = useMemo(
    () => [...EDITABLE_KEYS].some((key) => values[key] !== initialValues[key]),
    [values, initialValues],
  );

  useEffect(() => {
    if (!isEditing) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !saving) {
        setValues(initialValues);
        setIsEditing(false);
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (!saving && isDirty) {
          document.getElementById("commission-inquiry-save-btn")?.click();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isEditing, saving, initialValues, isDirty]);

  const updateField = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleCancel = () => {
    setValues(initialValues);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!isDirty || saving) return;

    const feeDirty = [...FEE_EDITABLE_KEYS].some(
      (key) => values[key] !== initialValues[key],
    );
    const courseDirty = [...EDITABLE_KEYS].some(
      (key) => !FEE_EDITABLE_KEYS.has(key) && values[key] !== initialValues[key],
    );

    setSaving(true);
    try {
      const tasks = [];

      if (courseDirty) {
        tasks.push(
          updateApplicationStudentCourseDetails({
            application_id: params.appId || application?.id,
            application_course_id:
              inquiry.applicationCourseId || course?.applicationCourseId,
            client_id: params.studentId || student?.id,
            provider_id: params.providerId || provider?.id,
            course_id: params.courseId || course?.id,
            type: params.type,
            student_id: values.studentId,
            dob: values.studentBirthDate,
            start_date: values.start,
            end_date: values.end,
            tuition_fee: values.tuitionFee,
            commissionable_tuition_fee: values.commissionable_tuition_fee,
          }),
        );
      }

      if (feeDirty) {
        tasks.push(
          upsertCommissionInquiryFees({
            application_id: params.appId || application?.id,
            client_id: params.studentId || student?.id,
            provider_id: params.providerId || provider?.id,
            application_course_id:
              inquiry.applicationCourseId || course?.applicationCourseId,
            application_fee: values.applicationFee,
            material_fee: values.materialFee,
            currency,
          }),
        );
      }

      await Promise.all(tasks);
      setIsEditing(false);
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Inquiry details updated.",
        timer: 1400,
        showConfirmButton: false,
      });
      if (typeof onSaved === "function") {
        onSaved({
          tuitionFee: values.tuitionFee,
          commissionable_tuition_fee: values.commissionable_tuition_fee,
          applicationFee: values.applicationFee,
          materialFee: values.materialFee,
        });
      }
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to update inquiry details.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const initials = getInitials(values.clientName || "Client");
  const commissionableTuitionLabel = displayTuition(
    values.commissionable_tuition_fee,
    currency,
  );
  const tuitionLabel = displayTuition(values.tuitionFee, currency);
  const applicationFeeLabel = displayTuition(values.applicationFee, currency);
  const materialFeeLabel = displayTuition(values.materialFee, currency);
  const applicationFeeStatus = inquiry.applicationFeeStatus || "none";
  const materialFeeStatus = inquiry.materialFeeStatus || "none";
  const showApplicationFeeBadge = Number(values.applicationFee) > 0;
  const showMaterialFeeBadge = Number(values.materialFee) > 0;
  const startLabel = displayDate(values.start);
  const endLabel = displayDate(values.end);
  const durationHint = resolveDurationLabel(values.start, values.end);
  const clientPath =
    student?.id || params.studentId
      ? `/clients/view-client-detail/${student?.id || params.studentId}`
      : null;

  return (
    <section
      className={`overflow-hidden rounded-xl border bg-white shadow-sm transition ${
        isEditing ? "border-[#007BFF]/40" : "border-slate-200"
      }`}
    >
      {/* Compact header — client name only */}
      <div className="flex items-center gap-2.5 border-b border-slate-100 bg-gradient-to-r from-[#F7FAFF] to-white px-3 py-2.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#007BFF] text-[11px] font-bold text-white">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h2 className="mb-0 truncate text-[14px] font-semibold text-slate-900">
              {values.clientName || "Client"}
            </h2>
            {isEditing ? <Chip tone="amber">Editing</Chip> : null}
            {isEditing && isDirty ? <Chip tone="amber">Unsaved</Chip> : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {!isEditing ? (
            <>
              {onOpenDocuments ? (
                <button
                  type="button"
                  onClick={onOpenDocuments}
                  className="relative inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-700 transition hover:border-[#007BFF]/30 hover:bg-[#007BFF]/5 hover:text-[#007BFF]"
                  style={{ boxShadow: "none" }}
                  title={
                    documentCount > 0
                      ? `View ${documentCount} document${documentCount === 1 ? "" : "s"}`
                      : "Client documents"
                  }
                >
                  <FaFileAlt size={10} />
                  Documents
                  {documentCount > 0 ? (
                    <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#007BFF] px-1 text-[9px] font-bold text-white">
                      {documentCount > 99 ? "99+" : documentCount}
                    </span>
                  ) : null}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="inline-flex h-8 items-center gap-1 rounded-lg bg-[#007BFF] px-2.5 text-[11px] font-semibold text-white transition hover:bg-[#0069D9]"
                style={{ border: "none" }}
              >
                <FaEdit size={10} />
                Edit
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
              >
                <FaTimes size={9} />
                Cancel
              </button>
              <button
                id="commission-inquiry-save-btn"
                type="button"
                onClick={handleSave}
                disabled={saving || !isDirty}
                className={`inline-flex h-8 items-center gap-1 rounded-lg px-2.5 text-[11px] font-semibold text-white ${
                  saving || !isDirty
                    ? "cursor-not-allowed bg-[#007BFF]/40"
                    : "bg-[#007BFF] hover:bg-[#0069D9]"
                }`}
                style={{ border: "none" }}
              >
                <FaCheck size={9} />
                {saving ? "Saving…" : "Save"}
              </button>
            </>
            )}
          </div>
        </div>

      {isEditing ? (
        <div className="cms-commission-profile-grid">
          <div className="border-b border-slate-100 bg-slate-50/40 px-3 py-2.5 lg:border-b-0 lg:border-r">
            <SectionLabel icon={FaUser}>Client</SectionLabel>
            <div className="space-y-2">
              <div>
                <p className="mb-0 text-[9px] font-medium text-slate-400">
                  Client name
                </p>
                <p className="mb-0 mt-0.5 text-[13px] font-semibold text-slate-900">
                  {values.clientName || "—"}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <FieldInput
                  label="Student ID"
                  type="text"
                  value={values.studentId}
                  disabled={saving}
                  onChange={(v) => updateField("studentId", v)}
                  inputRef={firstInputRef}
                />
                <FieldInput
                  label="DOB"
                  type="date"
                  value={values.studentBirthDate}
                  disabled={saving}
                  onChange={(v) => updateField("studentBirthDate", v)}
                />
              </div>
            </div>
          </div>

          <div className="border-b border-slate-100 bg-slate-50/40 px-3 py-2.5 lg:border-b-0 lg:border-r">
            <SectionLabel icon={FaGraduationCap}>Course</SectionLabel>
            <div className="space-y-2">
              <div>
                <p className="mb-0 text-[9px] font-medium text-slate-400">
                  Course name
                </p>
                <p className="mb-0 mt-0.5 line-clamp-2 text-[12px] font-semibold leading-snug text-slate-900">
                  {values.course || "—"}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <div className="inline-flex min-w-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1">
                  <FaUniversity size={8} className="shrink-0 text-slate-400" />
                  <div className="min-w-0">
                    <p className="mb-0 text-[8px] font-semibold uppercase text-slate-400">
                      Provider
                    </p>
                    <p className="mb-0 truncate text-[11px] font-semibold text-slate-700">
                      {values.providerName || "—"}
                    </p>
                  </div>
                </div>
                <div className="inline-flex min-w-0 items-center rounded-md border border-slate-200 bg-white px-2 py-1">
                  <div className="min-w-0">
                    <p className="mb-0 text-[8px] font-semibold uppercase text-slate-400">
                      Intake
                    </p>
                    <p className="mb-0 truncate text-[11px] font-semibold text-slate-700">
                      {values.intake || "—"}
                    </p>
                  </div>
                </div>
              </div>
              <FieldInput
                label="Total tuition fee"
                type="number"
                value={values.tuitionFee}
                disabled={saving}
                onChange={(v) => updateField("tuitionFee", v)}
              />

              <FieldInput
                label="Commissionable tuition fee"
                type="number"
                value={values.commissionable_tuition_fee}
                disabled={saving}
                onChange={(v) => updateField("commissionable_tuition_fee", v)}
              />
              <div className="grid grid-cols-2 gap-2">
                <FieldInput
                  label="Application fee"
                  type="number"
                  value={values.applicationFee}
                  disabled={saving}
                  onChange={(v) => updateField("applicationFee", v)}
                />
                <FieldInput
                  label="Material fee"
                  type="number"
                  value={values.materialFee}
                  disabled={saving}
                  onChange={(v) => updateField("materialFee", v)}
                />
              </div>
            </div>
        </div>

          <div className="bg-slate-50/40 px-3 py-2.5">
            <SectionLabel icon={FaCalendarAlt} hint={durationHint}>
              Duration
            </SectionLabel>
            <div className="space-y-2">
              <FieldInput
                label="Start"
                type="date"
                value={values.start}
                disabled={saving}
                onChange={(v) => updateField("start", v)}
              />
              <FieldInput
                label="End"
                type="date"
                value={values.end}
                disabled={saving}
                onChange={(v) => updateField("end", v)}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="cms-commission-profile-grid">
          {/* Client */}
          <div className="border-b border-slate-100 px-3 py-2.5 lg:border-b-0 lg:border-r">
            <SectionLabel icon={FaUser}>Client</SectionLabel>
            <div className="space-y-2">
              <div>
                <p className="mb-0 text-[9px] font-medium text-slate-400">
                  Client name
                </p>
                <p className="mb-0 mt-0.5 text-[13px] font-semibold text-slate-900">
                  {values.clientName || "—"}
                </p>
                {clientPath ? (
                  <Link
                    to={clientPath}
                    className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#007BFF] hover:underline"
                  >
                    Open client <FaArrowRight size={7} />
                  </Link>
                ) : null}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="mb-0 text-[9px] font-medium text-slate-400">
                    Student ID
                  </p>
                  <p className="mb-0 mt-0.5 text-[12px] font-semibold text-slate-800">
                    {values.studentId || "—"}
                  </p>
                </div>
            <div>
                  <p className="mb-0 text-[9px] font-medium text-slate-400">
                    DOB
                  </p>
                  <p className="mb-0 mt-0.5 text-[12px] font-semibold text-slate-800">
                    {displayDate(values.studentBirthDate)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Course + provider + intake + tuition */}
          <div className="border-b border-slate-100 px-3 py-2.5 lg:border-b-0 lg:border-r">
            <SectionLabel icon={FaGraduationCap}>Course</SectionLabel>
            <div className="space-y-2">
              <div>
                <p className="mb-0 text-[9px] font-medium text-slate-400">
                  Course name
                </p>
                <p className="mb-0 mt-0.5 line-clamp-2 text-[12px] font-semibold leading-snug text-slate-900">
                  {values.course || "—"}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <div className="inline-flex min-w-0 items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1">
                  <FaUniversity size={8} className="shrink-0 text-slate-400" />
                  <div className="min-w-0">
                    <p className="mb-0 text-[8px] font-semibold uppercase text-slate-400">
                      Provider
                    </p>
                    <p className="mb-0 truncate text-[11px] font-semibold text-slate-700">
                      {values.providerName || "—"}
                    </p>
                  </div>
                </div>
                <div className="inline-flex min-w-0 items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-1">
                  <div className="min-w-0">
                    <p className="mb-0 text-[8px] font-semibold uppercase text-slate-400">
                      Intake
                    </p>
                    <p className="mb-0 truncate text-[11px] font-semibold text-slate-700">
                      {values.intake || "—"}
                    </p>
                  </div>
                </div>
                <div className="inline-flex items-center rounded-md border border-[#007BFF]/20 bg-[#007BFF]/5 px-2 py-1">
                  <div>
                    <p className="mb-0 text-[8px] font-semibold uppercase text-[#007BFF]/70">
                      Total Tuition Fee
                    </p>
                    <p className="mb-0 text-[11px] font-bold tabular-nums text-[#007BFF]">
                      {tuitionLabel}
                    </p>
                  </div>
                </div>
                <div className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1">
                  <div>
                    <p className="mb-0 text-[8px] font-semibold uppercase text-emerald-600">
                      Commissionable Tuition Fee
                    </p>
                    <p className="mb-0 text-[11px] font-bold tabular-nums text-emerald-700">
                      {commissionableTuitionLabel}
                    </p>
                  </div>
                </div>
                <div className="inline-flex items-center rounded-md border border-violet-200 bg-violet-50 px-2 py-1">
                  <div className="min-w-0">
                    <p className="mb-0 text-[8px] font-semibold uppercase text-violet-600">
                      Application Fee
                    </p>
                    <div className="relative pr-9">
                      <p className="mb-0 text-[11px] font-bold tabular-nums text-violet-700">
                        {applicationFeeLabel}
                      </p>
                      {showApplicationFeeBadge ? (
                        <FeeStatusBadge status={applicationFeeStatus} />
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="inline-flex items-center rounded-md border border-orange-200 bg-orange-50 px-2 py-1">
                  <div className="min-w-0">
                    <p className="mb-0 text-[8px] font-semibold uppercase text-orange-600">
                      Material Fee
                    </p>
                    <div className="relative pr-9">
                      <p className="mb-0 text-[11px] font-bold tabular-nums text-orange-700">
                        {materialFeeLabel}
                      </p>
                      {showMaterialFeeBadge ? (
                        <FeeStatusBadge status={materialFeeStatus} />
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Duration */}
          <div className="px-3 py-2.5">
            <SectionLabel icon={FaCalendarAlt} hint={durationHint}>
              Duration
            </SectionLabel>
            <div className="relative rounded-lg border border-slate-200 bg-slate-50/80 p-2.5">
              <div
                className="absolute bottom-7 left-[1.15rem] top-7 w-px bg-slate-200"
                aria-hidden
              />
              <div className="relative mb-2.5 flex items-start gap-2">
                <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 rounded-full border-2 border-[#007BFF] bg-white" />
                <div className="min-w-0">
                  <p className="mb-0 text-[9px] font-medium text-slate-400">
                    Start
                  </p>
                  <p className="mb-0 text-[12px] font-semibold text-slate-800">
                    {startLabel}
                  </p>
                </div>
              </div>
              <div className="relative flex items-start gap-2">
                <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 rounded-full border-2 border-emerald-500 bg-white" />
                <div className="min-w-0">
                  <p className="mb-0 text-[9px] font-medium text-slate-400">
                    End
                  </p>
                  <p className="mb-0 text-[12px] font-semibold text-slate-800">
                    {endLabel}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
    </section>
  );
}

export default CommissionDetailProfileSidebar;
